import cn from 'classnames';
import { Field } from 'redux-form';
import connect from 'components/Shared/connect';
import { rm } from 'utils';
import * as rules from 'views/CheckoutPage/validation';
import { RenderCheckbox, RenderLabeledInput } from 'components/Shared/Form/inputs';
import { fieldStreet, fieldZipcode } from 'views/CheckoutPage/selector';
import ContactInfoPolicy from 'views/CheckoutPage/PolicyInformation/themes/BGH-RD3/contactInfoPolicy';
import ManualFormWrapper, { RenderFormProps } from '../../BGH-RD3/manualForm';
import { postAddressSuggestions, postZipcodeSuggestions } from 'services/ApiCheckoutPageService';
import SuggestionField, {
  addressFormat,
  zipFormat,
  zipParser
} from '../../BGH-RD3/suggestionField';
import { ToggleQuickstart } from '../../BGH-RD3/toggleQuickstart';
import { LeaveMessage } from '../../BGH-RD3/leaveMessage';
import RotForm from './rotForm';
import commonStyles from '../styles.scss';
import { CertificateForm } from 'views/CheckoutPage/ContactInfo/themes/BGH-RD3/certificateForm';
import { isMobileLayout } from 'components/Shared/responsive';
import { useSelector } from 'react-redux';
import { selectNameMaxLength } from 'views/CheckoutPage/ContactInfo/themes/BGH-RD3/selector';

interface OwnProps {
  toggleQuickStart: () => void;
}

interface MergedProps extends OwnProps {
  hasRotDeduction: boolean;
  isMobile: boolean;
}

type InternalRenderFormProps = MergedProps & {
  maxNameLength?: number;
};

const renderForm =
  ({ hasRotDeduction, isMobile, toggleQuickStart, maxNameLength }: InternalRenderFormProps) =>
  (props: RenderFormProps) => {
    const onAddressChange = (street: string) => {
      props.resetAddressValidation();
      return postAddressSuggestions(street);
    };
    const onZipCodeChange = (zipCode: string) => {
      props.resetAddressValidation();
      return postZipcodeSuggestions(zipCode);
    };

    return (
      <div>
        <ToggleQuickstart
          label={rm(
            'checkout_contact_info_address_automatic',
            rm('checkout_cellphone').toLowerCase()
          )}
          onClick={toggleQuickStart}
          className={commonStyles.toggleManual}
        />
        <Field
          name="firstName"
          validate={[rules.name, rules.nameLength(maxNameLength)]}
          className={cn(commonStyles.input, commonStyles.left)}
          onBlur={props.onBlur}
          component={RenderLabeledInput}
          props={{ customLabel: rm('checkout_firstname') }}
        />
        <Field
          name="lastName"
          validate={[rules.name, rules.nameLength(maxNameLength)]}
          className={cn(commonStyles.input, commonStyles.right)}
          onBlur={props.onBlur}
          component={RenderLabeledInput}
          props={{ customLabel: rm('checkout_lastname') }}
        />
        <SuggestionField
          name="street"
          className={commonStyles.input}
          validate={rules.address}
          label={rm('checkout_address')}
          onBlur={props.onBlur}
          onChange={onAddressChange}
          onSelect={props.suggestionSelected(fieldStreet)}
          format={addressFormat}
          props={{
            customValid: props.hasValidAddress
          }}
        />
        <SuggestionField
          name="zipCode"
          className={cn(commonStyles.input, commonStyles.left)}
          validate={rules.zip}
          label={rm('checkout_zipcode')}
          onBlur={props.onBlur}
          onChange={onZipCodeChange}
          onSelect={props.suggestionSelected(fieldZipcode)}
          format={zipFormat}
          props={{
            inputMode: 'numeric',
            customValid: props.hasValidAddress || props.hasValidZipCode
          }}
          parse={zipParser}
        />
        <Field
          name="city"
          validate={rules.city}
          className={cn(commonStyles.input, commonStyles.right)}
          onBlur={props.onBlur}
          onChange={props.resetAddressValidation}
          component={RenderLabeledInput}
          props={{
            customLabel: rm('checkout_city'),
            customValid: props.hasValidAddress || props.hasValidZipCode
          }}
        />
        <Field
          name="hasDeliveryAddress"
          onChange={props.deliveryAddressChanged}
          component={RenderCheckbox}
          label={rm('checkout_delivery_alternative_label')}
          className={commonStyles.checkboxWrapper}
        />
        <Field
          name="phoneNumber"
          type="tel"
          tooltip={rm('checkout_cellphone_help')}
          validate={rules.phone}
          className={cn(commonStyles.input, commonStyles.left)}
          onBlur={props.onBlur}
          component={RenderLabeledInput}
          props={{ customLabel: rm('checkout_cellphone') }}
          inputMode="tel"
        />
        <Field
          name="email"
          type="email"
          validate={rules.email}
          className={cn(commonStyles.input, commonStyles.right)}
          onBlur={props.onBlur}
          component={RenderLabeledInput}
          props={{ customLabel: rm('checkout_email_rd3') }}
        />
        <Field
          name="identityNumber"
          inputMode="numeric"
          pattern={isMobile ? '[0-9]*' : rules.identityNumberRegexString}
          className={cn(commonStyles.input, commonStyles.left)}
          validate={rules.identityNumber}
          component={RenderLabeledInput}
          onBlur={props.onBlur}
          props={{
            customLabel: rm('checkout_identityheader_private')
          }}
        />
        <p className={cn(commonStyles.indentityHelp, commonStyles.input, commonStyles.right)}>
          {rm('checkout_identityheader_help')}
        </p>
        <LeaveMessage className={commonStyles.message} onBlur={props.onBlur} />
        {hasRotDeduction && <RotForm isManual onBlur={props.onBlur} />}
        <CertificateForm />
        <ContactInfoPolicy />
      </div>
    );
  };

const ManualForm = (props: MergedProps) => {
  const maxNameLength = useSelector(selectNameMaxLength);

  return (
    <ManualFormWrapper
      formType="primary"
      renderForm={renderForm({ ...props, maxNameLength: maxNameLength })}
    />
  );
};

export default connect<MergedProps, OwnProps>(state => ({
  hasRotDeduction: state.cart.hasRotDeduction,
  isMobile: isMobileLayout(state)
}))(ManualForm);
